import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Bookmark, Share2, Check, RefreshCw, Plus, Eye } from "lucide-react";
import api, { PERSONALITIES, imageUrl } from "@/lib/api";
import { loadResult, saveResult } from "@/lib/session";
import { useAuth } from "@/context/AuthContext";
import ShareCard from "@/components/ShareCard";
import { Swatch } from "@/components/ColorSwatch";
import { recommendationAsset, assetForItem, pieceAssetColourAccurate, waysToWear } from "@/lib/categoryAssets";
import { WaysToWear } from "@/components/WaysToWear";
import { useReduceMotion } from "@/lib/motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TIER_HEADING = { Best: "MOIRÉ's best matches", Great: "More great options", Bold: "Bolder choices" };

// A single MOIRÉ FINDS row. For garment categories it gently slideshows the
// colour across silhouettes (label synced), staggered per row so the list feels
// alive without flipping in unison. Non-garment finds (shoes/bags) stay static.
const FindRow = ({ rec, big, isSelected, onSelect, index, reduce, ga, animate }) => {
  const slides = useMemo(() => (animate ? waysToWear(rec) : []), [animate, rec.color_hex]); // eslint-disable-line react-hooks/exhaustive-deps
  const canRotate = slides.length > 1;
  const [i, setI] = useState(canRotate ? index % slides.length : 0);
  useEffect(() => {
    if (reduce || !canRotate) return;
    const interval = 4200 + (index % 5) * 650;
    const t = setInterval(() => setI((p) => (p + 1) % slides.length), interval);
    return () => clearInterval(t);
  }, [reduce, canRotate, slides.length, index]);
  const cur = canRotate ? slides[i % slides.length] : null;
  const src = cur ? cur.src : ga.src;
  const label = cur ? cur.label : ga.label;
  return (
    <button onClick={() => onSelect(rec)} data-testid={`rec-${rec.rank}`}
      className={`w-full flex items-center gap-4 border p-3 text-left transition-colors ${
        isSelected ? "border-[#C8A97E] bg-[#111111]" : "border-[#F5F2EA]/10 hover:border-[#C8A97E]/50"}`}>
      <span className={`relative shrink-0 overflow-hidden border border-[#F5F2EA]/12 bg-[#F5F2EA] ${big ? "h-20 w-20" : "h-14 w-14"}`}>
        {src
          ? <AnimatePresence initial={false}>
              <motion.img key={src} src={src} alt={label} loading="lazy" data-testid={`rec-${rec.rank}-img`}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: reduce ? 0 : 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-cover" />
            </AnimatePresence>
          : <span className="block w-full h-full" style={{ backgroundColor: rec.color_hex }} />}
        <span className="absolute bottom-1 right-1 h-3.5 w-3.5 rounded-full border border-white/70 shadow-sm z-10" style={{ backgroundColor: rec.color_hex }} title={rec.color_name} />
      </span>
      <span className="flex-1 min-w-0">
        <span className={`block font-serif text-[#F5F2EA] truncate ${big ? "text-2xl" : "text-lg"}`}>{rec.color_name}</span>
        <span className="block font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] truncate" data-testid={`rec-${rec.rank}-label`}>{label}</span>
      </span>
      <span className={`font-serif text-[#C8A97E] shrink-0 ${big ? "text-4xl" : "text-2xl"}`}>{Number(rec.score).toFixed(1)}</span>
    </button>
  );
};

export default function Results() {
  const [result, setResult] = useState(null);
  const debug = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1";
  const [selected, setSelected] = useState(null);
  const [personality, setPersonality] = useState("classic");
  const [reveal, setReveal] = useState({ Great: false, Bold: false });
  const [savedRanks, setSavedRanks] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [signupPrompt, setSignupPrompt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [owned, setOwned] = useState(null);
  const { user } = useAuth();
  const reduce = useReduceMotion();
  const navigate = useNavigate();

  useEffect(() => {
    const r = loadResult();
    if (!r) { navigate("/match"); return; }
    setResult(r);
    setSelected(r.recommendations[0]);
    setPersonality(r.personality || "classic");
  }, [navigate]);

  const changePersonality = useCallback(async (p) => {
    if (!result || p === personality) return;
    setPersonality(p); setLoading(true);
    try {
      const { data } = await api.post("/match", {
        analysis: result.analysis, match_category: result.match_category,
        occasion: result.occasion || null, personality: p,
      });
      const merged = { ...data, image_url: result.image_url };
      setResult(merged); saveResult(merged);
      setSelected(data.recommendations[0]);
      setReveal({ Great: false, Bold: false });
    } catch { toast.error("Couldn't restyle just now."); }
    finally { setLoading(false); }
  }, [result, personality]);

  useEffect(() => { setOwned(null); }, [selected?.rank]);

  if (!result || !selected) return null;
  const { analysis, recommendations } = result;
  const byTier = (t) => recommendations.filter((r) => r.tier === t);
  const pickGa = recommendationAsset(selected, result.match_category, personality, result.occasion);
  // garment finds gently slideshow; shoes/bags/accessories stay static
  const findsAnimate = !/shoe|boot|heel|sneaker|loafer|sandal|bag|purse|clutch|tote|accessor|jewel|belt|scarf|hat|cap|sunglass|watch|glove/i.test(result.match_category || "");

  const doSave = async (rec) => {
    if (!user) { setSignupPrompt(true); return; }
    try {
      await api.post("/looks", {
        analysis, match_category: result.match_category, occasion: result.occasion,
        recommendation: rec, item_id: result.session_id, image_path: result.image_url,
      });
      setSavedRanks((s) => [...s, rec.rank]);
      toast.success("Saved to your looks.");
    } catch { toast.error("Couldn't save that look."); }
  };

  const seeTheLook = (rec) => {
    sessionStorage.setItem("moire.lookRequest", JSON.stringify({
      source_analysis: analysis,
      recommendation: { color_name: rec.color_name, color_hex: rec.color_hex, match_category: result.match_category },
      occasion: result.occasion || null,
    }));
    navigate("/look");
  };

  const checkWardrobe = async () => {
    setOwned("loading");
    try {
      const { data } = await api.post("/wardrobe/closest", {
        color_hex: selected.color_hex, color_name: selected.color_name, match_category: result.match_category });
      setOwned(data);
    } catch { setOwned([]); }
  };

  const doShare = (rec) => {
    setShareData({ baseColor: analysis.primary_color_name, baseHex: analysis.primary_color_hex,
      matchColor: rec.color_name, matchHex: rec.color_hex, score: rec.score });
    setShareOpen(true);
  };

  const TierList = ({ tier }) => {
    const items = byTier(tier);
    if (!items.length) return null;
    return (
      <div className="space-y-2.5">
        {items.map((rec) => {
          const ga = recommendationAsset(rec, result.match_category, personality, result.occasion);
          return (
            <FindRow key={rec.rank} rec={rec} big={rec.rank === 1}
              isSelected={selected.rank === rec.rank} onSelect={setSelected}
              index={rec.rank} reduce={reduce} ga={ga} animate={findsAnimate} />
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
      <button onClick={() => navigate("/match")} data-testid="results-back"
        className="flex items-center gap-2 font-sans text-xs tracking-luxe uppercase text-[#8A8170] hover:text-[#C8A97E] mb-8">
        <ArrowLeft size={14} strokeWidth={1.5} /> Try Another Item
      </button>

      <div className="flex items-center gap-5 border-b border-[#F5F2EA]/10 pb-8">
        {(() => {
          const pieceImg = result.image_url || pieceAssetColourAccurate(analysis);
          return (
            <span className="h-20 w-20 shrink-0 overflow-hidden border border-[#F5F2EA]/15 bg-[#F5F2EA] relative" data-testid="the-piece">
              {pieceImg
                ? <img src={pieceImg} alt={`${analysis.primary_color_name} ${analysis.item}`} className="h-full w-full object-cover" />
                : <span className="block h-full w-full" style={{ backgroundColor: analysis.primary_color_hex }} />}
              <span className="absolute bottom-1 right-1 h-3.5 w-3.5 rounded-full border border-white/70 shadow-sm" style={{ backgroundColor: analysis.primary_color_hex }} title={analysis.primary_color_name} />
            </span>
          );
        })()}
        <div>
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">MOIRÉ's picks</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#F5F2EA]">{analysis.primary_color_name} {analysis.item}</h1>
          <p className="font-sans text-xs text-[#8A8170] mt-1">The strongest combinations first. Explore as far as your style takes you.</p>
        </div>
      </div>

      {/* style personality filter */}
      <div className="mt-8">
        <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-3">How do you want to wear it?</p>
        <div className="flex flex-wrap gap-2">
          {PERSONALITIES.map((p) => (
            <button key={p.id} onClick={() => changePersonality(p.id)} data-testid={`personality-${p.id}`}
              className={`px-4 py-2 font-sans text-xs tracking-luxe uppercase border transition-colors ${
                personality === p.id ? "border-[#C8A97E] bg-[#C8A97E] text-[#0D0D0D]" : "border-[#F5F2EA]/15 text-[#F5F2EA]/75 hover:border-[#C8A97E]"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`grid lg:grid-cols-[1.15fr_0.85fr] gap-10 mt-10 ${loading ? "opacity-50 pointer-events-none" : ""}`}>
        <div>
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-3">{TIER_HEADING.Best}</p>
          <TierList tier="Best" />

          {!reveal.Great && byTier("Great").length > 0 && (
            <button onClick={() => setReveal((r) => ({ ...r, Great: true }))} data-testid="explore-more"
              className="mt-6 flex items-center gap-2 font-sans text-xs tracking-luxe uppercase text-[#C8A97E] border border-[#C8A97E]/40 px-5 py-3 hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors">
              <Plus size={14} strokeWidth={1.5} /> Explore More
            </button>
          )}
          <AnimatePresence>
            {reveal.Great && byTier("Great").length > 0 && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
                <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mt-8 mb-3">{TIER_HEADING.Great}</p>
                <TierList tier="Great" />
              </motion.div>
            )}
          </AnimatePresence>

          {reveal.Great && !reveal.Bold && byTier("Bold").length > 0 && (
            <button onClick={() => setReveal((r) => ({ ...r, Bold: true }))} data-testid="show-bolder"
              className="mt-6 flex items-center gap-2 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/70 border border-[#F5F2EA]/15 px-5 py-3 hover:border-[#C8A97E] transition-colors">
              Feeling bold? Show bolder choices
            </button>
          )}
          <AnimatePresence>
            {reveal.Bold && byTier("Bold").length > 0 && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
                <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mt-8 mb-3">{TIER_HEADING.Bold}</p>
                <TierList tier="Bold" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* detail panel */}
        <div className="lg:sticky lg:top-24 h-fit">
          <motion.div key={selected.rank} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="border border-[#C8A97E]/40 bg-[#111111]" data-testid="detail-panel">
            <div className="flex">
              <div className="w-28 shrink-0 relative bg-[#F5F2EA]">
                {pickGa.src
                  ? <img src={pickGa.src} alt={pickGa.label} className="w-full h-full object-cover" />
                  : <div className="w-full h-full" style={{ backgroundColor: selected.color_hex }} />}
                <span className="absolute bottom-1.5 right-1.5 h-4 w-4 rounded-full border border-white/70 shadow-sm" style={{ backgroundColor: selected.color_hex }} title={selected.color_name} />
              </div>
              <div className="flex-1 p-6 flex items-start justify-between">
                <div>
                  <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">MOIRÉ's Pick</p>
                  <h3 className="font-serif text-3xl text-[#F5F2EA] mt-1">{selected.color_name}</h3>
                  <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mt-1.5">{pickGa.label}</p>
                </div>
                <div className="text-right">
                  <div className="font-serif text-4xl text-[#C8A97E] leading-none">{Number(selected.score).toFixed(1)}</div>
                  <div className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] mt-1">/ 10</div>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6">
              {selected.take && (<div className="mb-4">
                <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-1">MOIRÉ's Take</p>
                <p className="font-serif italic text-lg text-[#F5F2EA] leading-relaxed">{selected.take}</p></div>)}
              {selected.why && (<div className="mb-5">
                <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-1">Why This Works</p>
                <p className="font-sans text-sm text-[#F5F2EA]/75 leading-relaxed">{selected.why}</p></div>)}
              <WaysToWear rec={selected} />
              {debug && selected.score_breakdown && Object.keys(selected.score_breakdown).length > 0 && (
                <div className="mb-5 border border-dashed border-[#8A8170]/40 px-4 py-3" data-testid="rec-debug">
                  <p className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] mb-2">Debug · {selected.harmony} · components</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {Object.entries(selected.score_breakdown).map(([k, v]) => (
                      <div key={k} className="flex justify-between font-mono text-[11px] text-[#F5F2EA]/70">
                        <span>{k}</span><span>{Number(v).toFixed(2)}</span>
                      </div>))}
                  </div>
                </div>
              )}
              {selected.complete_look?.length > 0 && (
                <div className="border-t border-[#F5F2EA]/10 pt-5">
                  <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-3">Complete This Look</p>
                  <p className="font-serif text-xl text-[#F5F2EA] leading-relaxed">{selected.complete_look[0]}
                    {selected.complete_look[1] && <><span className="text-[#8A8170]"> with </span>{selected.complete_look[1]}</>}</p>
                  {selected.complete_look.length > 2 && (
                    <p className="font-sans text-sm text-[#8A8170] mt-2">finish with {selected.complete_look.slice(2).join(", ")}</p>)}
                  {selected.look_note && <p className="font-serif italic text-[#C8A97E] mt-4">“{selected.look_note}”</p>}
                </div>)}
              <div className="flex flex-wrap gap-2.5 mt-6">
                <button onClick={() => seeTheLook(selected)} data-testid="see-the-look-btn"
                  className="flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-4 py-2.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">
                  <Eye size={14} strokeWidth={1.5} /> See The Look</button>
                <button onClick={() => doSave(selected)} data-testid="save-look-btn"
                  className="flex items-center gap-2 border border-[#F5F2EA]/15 px-4 py-2.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">
                  {savedRanks.includes(selected.rank) ? <Check size={14} strokeWidth={1.5} /> : <Bookmark size={14} strokeWidth={1.5} />}
                  {savedRanks.includes(selected.rank) ? "Saved" : "Save Look"}</button>
                <button onClick={() => doShare(selected)} data-testid="share-look-btn"
                  className="flex items-center gap-2 border border-[#F5F2EA]/15 px-4 py-2.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">
                  <Share2 size={14} strokeWidth={1.5} /> Share</button>
                <button onClick={() => navigate("/match")} data-testid="try-another-btn"
                  className="flex items-center gap-2 border border-[#F5F2EA]/15 px-4 py-2.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">
                  <RefreshCw size={14} strokeWidth={1.5} /> Try Another</button>
                {user && (
                  <button onClick={checkWardrobe} data-testid="check-wardrobe-btn"
                    className="flex items-center gap-2 border border-[#F5F2EA]/15 px-4 py-2.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">
                    Check My Wardrobe</button>
                )}
              </div>

              {owned && owned !== "loading" && (
                <div className="mt-5 border-t border-[#F5F2EA]/10 pt-4" data-testid="owned-matches">
                  {owned.length === 0 ? (
                    <p className="font-sans text-sm text-[#8A8170]">Nothing in your wardrobe matches this shade yet.</p>
                  ) : (
                    <>
                      <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-3">You already own a match</p>
                      <div className="space-y-2">
                        {owned.slice(0, 3).map((o) => (
                          <div key={o.item.id} className="flex items-center gap-3">
                            {o.item.image_url
                              ? <img src={imageUrl(o.item.image_url)} alt={o.item.name} loading="lazy" className="h-10 w-10 object-cover border border-[#F5F2EA]/15" />
                              : <span className="h-10 w-10 border border-[#F5F2EA]/15" style={{ backgroundColor: o.item.primary_color_hex }} />}
                            <span className="font-serif text-lg text-[#F5F2EA] flex-1 truncate">{o.item.name}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <ShareCard open={shareOpen} onOpenChange={setShareOpen} data={shareData} />

      <Dialog open={signupPrompt} onOpenChange={setSignupPrompt}>
        <DialogContent className="bg-[#0D0D0D] border border-[#C8A97E]/50 shadow-2xl shadow-black/60 max-w-sm" data-testid="signup-prompt">
          <div className="text-center p-2">
            <DialogTitle className="font-serif text-3xl text-[#F5F2EA] font-normal">Keep this look</DialogTitle>
            <DialogDescription className="font-sans text-sm text-[#8A8170] mt-3 leading-relaxed">
              Create a free account to save your pairings and revisit them anytime.
            </DialogDescription>
            <div className="flex flex-col gap-3 mt-8">
              <button onClick={() => navigate("/signup")} data-testid="prompt-signup"
                className="bg-[#C8A97E] text-[#0D0D0D] py-3.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">Create Account</button>
              <button onClick={() => navigate("/signin")} data-testid="prompt-signin"
                className="font-sans text-xs tracking-luxe uppercase text-[#8A8170] hover:text-[#C8A97E]">I already have one</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
